"use client";

import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Github,
  Linkedin,
  Mail,
  ArrowUpRight,
  Heart,
  Phone,
  MapPin,
  Instagram,
  Facebook,
} from "lucide-react";
import { personalInfo, navLinks } from "../../data/mock";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    personalInfo.github && {
      icon: Github,
      href: personalInfo.github,
      label: "GitHub",
    },
    personalInfo.linkedin && {
      icon: Linkedin,
      href: personalInfo.linkedin,
      label: "LinkedIn",
    },
    {
      icon: Instagram,
      href: "https://www.instagram.com/_gauranshjaroli_/",
      label: "Instagram",
    },
    {
      icon: Facebook,
      href: "https://www.facebook.com/gauransh.jaroli?mibextid=eHce3h",
      label: "Facebook",
    },
    {
      icon: Phone,
      href: "https://wa.me/919521347419",
      label: "WhatsApp",
    },
    personalInfo.email && {
      icon: Mail,
      href: `mailto:${personalInfo.email}`,
      label: "Email",
    },
  ].filter(Boolean);

  return (
    <footer className="relative bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 border-t border-white/10 overflow-hidden">
      {/* Glow */}
      <div className="absolute -top-40 -left-40 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-emerald-400/10 rounded-full blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* ================= BRAND ================= */}
          <div className="md:col-span-2">
            <Link to="/" className="inline-block mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center">
                  <span className="text-slate-900 font-bold text-lg">GJ</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-lg">
                    {personalInfo.name}
                  </p>
                  <p className="text-xs text-slate-400">
                    Web Developer & Business Solutions
                  </p>
                </div>
              </div>
            </Link>

            <p className="text-slate-300 text-sm leading-relaxed max-w-md">
              I build websites and systems that help businesses grow online —
              from simple sites to full ordering systems.
            </p>

            {/* Social Links */}
            <div className="flex gap-3 mt-6 flex-wrap">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -4, scale: 1.1 }}
                  className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-300 hover:bg-gradient-to-br hover:from-emerald-400 hover:to-blue-500 hover:text-black transition"
                  aria-label={social.label}
                >
                  <social.icon size={18} />
                </motion.a>
              ))}
            </div>
          </div>

          {/* ================= QUICK LINKS ================= */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-slate-300 hover:text-emerald-400 text-sm transition flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight
                      size={14}
                      className="opacity-0 group-hover:opacity-100 transition"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* ================= CONTACT ================= */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>

            <ul className="space-y-3 text-sm">
              {/* Email */}
              <li>
                <a
                  href={`mailto:${personalInfo.email}`}
                  className="text-slate-300 hover:text-emerald-400 transition flex items-center gap-2"
                >
                  <Mail size={16} />
                  {personalInfo.email}
                </a>
              </li>

              {/* Phone */}
              <li>
                <a
                  href="tel:+919521347419"
                  className="text-slate-300 hover:text-emerald-400 transition flex items-center gap-2"
                >
                  <Phone size={16} />
                  +91 95213 47419
                </a>
              </li>

              {/* WhatsApp */}
              <li>
                <a
                  href="https://wa.me/919521347419"
                  target="_blank"
                  className="text-slate-300 hover:text-emerald-400 transition flex items-center gap-2"
                >
                  <Phone size={16} />
                  WhatsApp Chat
                </a>
              </li>

              {/* Location + Map */}
              <li>
                <a
                  href="https://maps.google.com/?q=Udaipur,Rajasthan"
                  target="_blank"
                  className="text-slate-300 hover:text-emerald-400 transition flex items-center gap-2"
                >
                  <MapPin size={16} />
                  {personalInfo.location}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* ================= BOTTOM ================= */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-400 text-sm">
            © {currentYear} {personalInfo.name}. All rights reserved.
          </p>

          <p className="text-slate-400 text-sm flex items-center gap-1">
            Crafted with <Heart size={14} className="text-red-400" /> using
            React & Tailwind
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
